"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  FileSpreadsheet,
  FileText,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
} from "lucide-react";
import { exportToExcel, exportToPDF } from "@/lib/excelExport";
import { formatCurrency } from "@/lib/utils";
import { format } from "date-fns";
import type { AppointmentDetail, StaffInfo } from "@/services/reportsService";

interface DailyCompletedDashboardProps {
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    staff: StaffInfo[];
    dailyData: Array<{
      date: string;
      byStaff: { [staffName: string]: AppointmentDetail[] };
      byStaffCount: { [staffName: string]: number };
      total: number;
    }>;
    totals: {
      byStaff: { [staffName: string]: number };
      total: number;
    };
  };
}

const COLORS = [
  "#0ea5e9",
  "#8b5cf6",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#6366f1",
  "#ec4899",
  "#14b8a6",
];

function StatusBadge({ status }: { status: string }) {
  const s = status?.toUpperCase();
  if (s === "COMPLETED" || s === "FINISHED")
    return (
      <Badge className="bg-green-100 text-green-700 border-green-300">
        {status}
      </Badge>
    );
  return (
    <Badge variant="outline" className="text-slate-600">
      {status}
    </Badge>
  );
}

export function DailyCompletedDashboard({
  data,
}: DailyCompletedDashboardProps) {
  const staffNames = data.staff.map((s) => s.name);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  const toggleDate = (date: string) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  // Flatten all appointments for a single date row across all staff
  const getAppointmentsForDate = (
    row: (typeof data.dailyData)[0],
  ): AppointmentDetail[] => {
    return Object.values(row.byStaff).flat();
  };

  const getExportData = () => {
    const exportData = data.dailyData.map((row) => {
      const rowData: Record<string, string | number> = {
        Date: format(new Date(row.date), "dd/MM/yyyy"),
      };
      staffNames.forEach((name) => {
        rowData[name] = row.byStaffCount[name] || 0;
      });
      rowData["Total"] = row.total;
      return rowData;
    });

    const totalsRow: Record<string, string | number> = { Date: "TOTAL" };
    staffNames.forEach((name) => {
      totalsRow[name] = data.totals.byStaff[name] || 0;
    });
    totalsRow["Total"] = data.totals.total;
    exportData.push(totalsRow);

    return exportData;
  };

  const handleExportExcel = () => {
    exportToExcel(getExportData(), "Daily_Completed_Orders", "Completed Orders");
  };

  const handleExportPDF = () => {
    exportToPDF(getExportData(), "Daily_Completed_Orders", {
      title: "Daily Completed Orders Report",
      subtitle: "Completed orders breakdown by staff",
      dateRange: data.dateRange,
    });
  };

  // Chart data uses counts
  const chartData = data.dailyData.map((row) => ({
    date: row.date,
    ...row.byStaffCount,
  }));

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        {staffNames.slice(0, 3).map((name) => (
          <Card key={name}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{name}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {data.totals.byStaff[name] || 0}
              </div>
              <p className="text-xs text-muted-foreground">Completed orders</p>
            </CardContent>
          </Card>
        ))}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Grand Total</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {data.totals.total}
            </div>
            <p className="text-xs text-muted-foreground">All completed orders</p>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Daily Completed Orders by Staff</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExportExcel}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF}>
              <FileText className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => format(new Date(value), "dd MMM")}
              />
              <YAxis />
              <Tooltip
                labelFormatter={(value) =>
                  format(new Date(value), "dd MMM yyyy")
                }
              />
              <Legend />
              {staffNames.map((name, index) => (
                <Bar
                  key={name}
                  dataKey={name}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Daily Breakdown with expandable rows */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle>Daily Breakdown</CardTitle>
            <span className="text-xs text-muted-foreground">
              — click a row to see appointment details
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Date</TableHead>
                {staffNames.map((name) => (
                  <TableHead key={name} className="text-right">
                    {name}
                  </TableHead>
                ))}
                <TableHead className="text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.dailyData.map((row) => {
                const isExpanded = expandedDates.has(row.date);
                const allAppts = getAppointmentsForDate(row);
                const hasAppts = allAppts.length > 0;

                return (
                  <>
                    {/* Main date row */}
                    <TableRow
                      key={row.date}
                      className={`cursor-pointer transition-colors ${
                        isExpanded
                          ? "bg-green-50 dark:bg-green-950/20"
                          : "hover:bg-muted/40"
                      }`}
                      onClick={() => hasAppts && toggleDate(row.date)}
                    >
                      <TableCell className="py-2">
                        {hasAppts ? (
                          isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )
                        ) : null}
                      </TableCell>
                      <TableCell className="font-medium">
                        {format(new Date(row.date), "dd MMM yyyy")}
                      </TableCell>
                      {staffNames.map((name) => {
                        const count = row.byStaffCount[name] || 0;
                        return (
                          <TableCell key={name} className="text-right">
                            {count > 0 ? (
                              <Badge className="bg-green-100 text-green-700 border-green-300 cursor-pointer">
                                {count}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">0</span>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-right font-semibold text-green-700">
                        {row.total}
                      </TableCell>
                    </TableRow>

                    {/* Expanded sub-table */}
                    {isExpanded && (
                      <TableRow key={`${row.date}-expanded`}>
                        <TableCell
                          colSpan={staffNames.length + 3}
                          className="p-0"
                        >
                          <div className="mx-6 my-2 rounded-lg border border-green-200 bg-green-50/50 dark:bg-green-950/10 overflow-hidden">
                            <div className="flex items-center gap-2 px-4 py-2 border-b border-green-200 bg-green-100/60 dark:bg-green-950/20">
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                              <span className="text-sm font-semibold text-green-800 dark:text-green-300">
                                {allAppts.length} completed appointment
                                {allAppts.length !== 1 ? "s" : ""} on{" "}
                                {format(new Date(row.date), "dd MMM yyyy")}
                              </span>
                            </div>
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-transparent hover:bg-transparent">
                                  <TableHead className="text-xs font-semibold text-green-700">
                                    Ref / ID
                                  </TableHead>
                                  <TableHead className="text-xs font-semibold text-green-700">
                                    Customer
                                  </TableHead>
                                  <TableHead className="text-xs font-semibold text-green-700">
                                    Vehicle
                                  </TableHead>
                                  <TableHead className="text-xs font-semibold text-green-700">
                                    Staff
                                  </TableHead>
                                  <TableHead className="text-xs font-semibold text-green-700">
                                    Status
                                  </TableHead>
                                  <TableHead className="text-xs font-semibold text-green-700 text-right">
                                    Amount
                                  </TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {allAppts.map((apt) => (
                                  <TableRow
                                    key={apt.id}
                                    className="bg-transparent hover:bg-green-100/40 dark:hover:bg-green-950/20"
                                  >
                                    <TableCell className="text-xs font-mono text-slate-500">
                                      {apt.bookingRef ||
                                        apt.id.slice(0, 8) + "…"}
                                    </TableCell>
                                    <TableCell className="text-sm font-medium">
                                      {apt.customerName}
                                    </TableCell>
                                    <TableCell className="text-sm">
                                      {apt.vehiclePlate}
                                    </TableCell>
                                    <TableCell className="text-sm">
                                      {apt.staffName}
                                    </TableCell>
                                    <TableCell>
                                      <StatusBadge status={apt.status} />
                                    </TableCell>
                                    <TableCell className="text-right text-sm font-medium">
                                      {apt.amount > 0
                                        ? formatCurrency(apt.amount)
                                        : "—"}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                );
              })}

              {/* Totals row */}
              <TableRow className="bg-muted/50 font-bold border-t-2">
                <TableCell />
                <TableCell>TOTAL</TableCell>
                {staffNames.map((name) => (
                  <TableCell key={name} className="text-right">
                    {data.totals.byStaff[name] || 0}
                  </TableCell>
                ))}
                <TableCell className="text-right text-green-700">
                  {data.totals.total}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
